import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mountain, Layers, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  {
    icon: Mountain,
    title: "Geotechnical Boring Technician",
    pay: "Depending on experience",
    points: ["Operate truck-mounted drill rigs", "Collect and log soil samples", "Assist with boring logs and site setup"],
    reqs: ["Valid driver's license", "Ability to lift 50 lbs and work outdoors"],
  },
  {
    icon: Layers,
    title: "Concrete & Asphalt Coring Technician",
    pay: "Depending on experience",
    points: ["Run diamond core rigs on pavements and structures", "Extract, label, and document cores", "Set up traffic control and work zones"],
    reqs: ["Valid driver's license", "Ability to lift 50 lbs and work outdoors"],
  },
];

const initial = { name: "", phone: "", email: "", position: "", experience: "", message: "" };

const inputCls =
  "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-[#FF5A00] transition-shadow duration-200";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2";

export default function Careers() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, form);
      toast.success("Application received! We'll review it and be in touch.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please call us at (832) 661-1121.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="careers" className="py-24 lg:py-32 bg-white" data-testid="careers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-4 font-mono-tech" data-testid="careers-overline">
            Careers • 08
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="careers-heading">
            Join the Crew
          </h2>
          <p className="mt-6 text-base text-slate-600 leading-relaxed">
            We're hiring field technicians across Greater Houston. Pick a role and apply below.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6" data-testid="careers-roles">
            {roles.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.1, ease: "easeOut" }}
                className="bg-[#F4F5F7] border border-slate-200 border-l-4 border-l-[#FF5A00] rounded-sm p-8"
                data-testid={`career-role-${i}`}
              >
                <div className="flex items-center gap-4">
                  <r.icon className="w-9 h-9 text-[#FF5A00]" />
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#0F172A]">{r.title}</h3>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#FF5A00] mt-1" data-testid={`career-pay-${i}`}>
                      Pay: {r.pay}
                    </p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2">
                  {r.points.map((p) => (
                    <li key={p} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-[#FF5A00] font-bold">•</span> {p}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs font-bold uppercase tracking-wider text-[#0F172A]">Requirements</p>
                <ul className="mt-2 space-y-2">
                  {r.reqs.map((req) => (
                    <li key={req} className="text-sm text-slate-600 flex items-start gap-2" data-testid={`career-req-${i}-${req.split(" ")[0].toLowerCase()}`}>
                      <span className="text-[#0F172A] font-bold">•</span> {req}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="lg:col-span-7 bg-white border border-slate-200 rounded-sm p-8 lg:p-10 shadow-[6px_6px_0px_#F4F5F7]"
            data-testid="careers-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="c-name" className={labelCls}>Name *</label>
                <input id="c-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="careers-input-name" />
              </div>
              <div>
                <label htmlFor="c-phone" className={labelCls}>Phone *</label>
                <input id="c-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(832) 555-0000" data-testid="careers-input-phone" />
              </div>
              <div>
                <label htmlFor="c-email" className={labelCls}>Email *</label>
                <input id="c-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="careers-input-email" />
              </div>
              <div>
                <label htmlFor="c-position" className={labelCls}>Position *</label>
                <select id="c-position" required value={form.position} onChange={set("position")} className={inputCls} data-testid="careers-select-position">
                  <option value="" disabled>Select a position</option>
                  {roles.map((r) => (
                    <option key={r.title} value={r.title}>{r.title}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="c-experience" className={labelCls}>Years of Experience</label>
                <input id="c-experience" value={form.experience} onChange={set("experience")} className={inputCls} placeholder="e.g., 3 years drilling / coring" data-testid="careers-input-experience" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="c-message" className={labelCls}>Tell Us About Yourself</label>
                <textarea id="c-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Certifications, equipment you've run, availability..." data-testid="careers-input-message" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="careers-submit-button"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider px-10 py-4 rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#0F172A]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {loading ? "Sending..." : "Submit Application"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
